import {
  type CompanyContactRole,
  type SupplierBuildingSizeUnit,
  type SupplierIndustryKind,
  type SupplierMeasurementEquipment,
  type SupplierOperationKind,
  type SupplierRevenueRange,
  QA_SKIP_CERTS,
  SUPPLIER_CERTIFICATE_LABELS
} from '@tandemone/types'

export { SUPPLIER_CERTIFICATE_LABELS }

export type LabeledOption = {
  label: string
  description: string
}

/** legacy operation labels, read-only. drives the legacy branch of the supplier
 *  profile for suppliers that predate the capability taxonomy. no picker uses
 *  this anymore, onboarding writes through the capabilities selector.
 */
export const SUPPLIER_OPERATION_OPTIONS: Record<
  SupplierOperationKind,
  LabeledOption
> = {
  additive_manufacturing: {
    label: 'Additive Manufacturing (3D Printing)',
    description: '3D printing and additive manufacturing processes'
  },
  aluminum_extrusions: {
    label: 'Aluminum Extrusions',
    description: 'Aluminum extrusion forming and finishing'
  },
  brazing: {
    label: 'Brazing',
    description: 'Brazing and joining processes'
  },
  castings: {
    label: 'Castings',
    description: 'Sand, investment, and die casting'
  },
  commercial_off_shelf_cots: {
    label: 'Commercial Off-the-Shelf (COTS)',
    description: 'Standardized, commercially available components'
  },
  cutting_and_frame_assembly: {
    label: 'Cutting and Frame Assembly',
    description: 'Cutting and frame fabrication'
  },
  die_design_and_fabrication: {
    label: 'Die Design and Fabrication',
    description: 'Die design and tooling fabrication'
  },
  electrical_component_assembly: {
    label: 'Electrical Component Assembly',
    description: 'Electrical assembly and wiring'
  },
  heat_treat_and_annealing: {
    label: 'Heat Treat and Annealing',
    description: 'Heat treatment and annealing processes'
  },
  industrial_machinery_and_equipment: {
    label: 'Industrial Machinery and Equipment',
    description: 'Manufacturing and supplying industrial machinery'
  },
  machining: {
    label: 'Machining',
    description: 'CNC machining, milling, turning, grinding'
  },
  metrology_calibration_cmm_gages: {
    label: 'Metrology, Calibration, CMM, Gages',
    description: 'Precision measurement and calibration'
  },
  ndt_testing_inspection_welding: {
    label: 'NDT Testing, Inspection, Welding',
    description: 'Non-destructive testing, inspection, and welding'
  },
  none_of_the_above: {
    label: 'None of the above',
    description: 'My business does not offer any of the listed operations.'
  },
  original_equipment_manufacturer: {
    label: 'Original Equipment Manufacturer (OEM)',
    description: 'Full original equipment manufacturing'
  },
  plastic_injection_molding: {
    label: 'Plastic Injection Molding',
    description: 'Plastic injection molding processes'
  },
  plating_painting_coating_finishing: {
    label: 'Plating, Painting, Coating, Finishing',
    description: 'Surface treatment and finishing'
  },
  printed_circuit_boards: {
    label: 'Printed Circuit Boards (PCBs)',
    description: 'PCB design and manufacturing'
  },
  raw_material_bar_stock: {
    label: 'Raw Material / Bar Stock',
    description: 'Supply of raw materials and bar stock'
  },
  sheet_metal_fabrication: {
    label: 'Sheet Metal Fabrication',
    description: 'Sheet metal forming, bending, and assembly'
  },
  stamping: {
    label: 'Stamping',
    description: 'Metal stamping and progressive die work'
  },
  tooling_components_fixtures_gaging: {
    label: 'Tooling, Components, Fixtures, Gaging',
    description: 'Tooling and fixture manufacturing'
  },
  wire_and_ribbon_processing: {
    label: 'Wire and Ribbon Processing',
    description: 'Wire drawing, coating, rolling, and spooling'
  }
}

export const SUPPLIER_INDUSTRY_OPTIONS: Record<
  SupplierIndustryKind,
  LabeledOption
> = {
  aerospace_and_defense: {
    label: 'Aerospace & Defense',
    description:
      'Aircraft and defense systems including drones, avionics, military platforms, and related components'
  },
  agriculture_and_farming_equipment: {
    label: 'Agriculture & Farming Equipment',
    description:
      'Agricultural machinery and systems including tractors, harvesters, irrigation systems, and farm equipment'
  },
  automotive_transportation_and_mobility: {
    label: 'Automotive, Transportation & Mobility',
    description:
      'Vehicles and mobility systems including road and transport equipment like rail, trailers, and material handling'
  },
  construction_and_infrastructure: {
    label: 'Construction & Infrastructure',
    description:
      'Equipment and systems used to build and maintain infrastructure including heavy equipment, HVAC, elevators, and cranes'
  },
  consumer_goods_and_appliances: {
    label: 'Consumer Goods & Appliances',
    description:
      'Consumer products and home equipment including appliances, power tools, and furniture mechanisms'
  },
  electronics_and_electrical_equipment: {
    label: 'Electronics & Electrical Equipment',
    description:
      'Electrical and electronic products and components including consumer electronics, control panels, wiring, and semiconductors'
  },
  energy_and_power_generation: {
    label: 'Energy & Power Generation',
    description:
      'Energy generation and distribution systems including renewables, turbines, and power equipment'
  },
  marine_and_marine_equipment: {
    label: 'Marine & Marine Equipment',
    description:
      'Maritime systems and equipment including vessels, marine engines, and offshore systems'
  },
  medical_devices_and_healthcare_equipment: {
    label: 'Medical Devices & Healthcare Equipment',
    description:
      'Healthcare products and equipment including diagnostic machines, surgical tools, hospital equipment, and medical devices'
  },
  none_of_the_above: {
    label: 'None of the above',
    description: 'My business does not serve any of the listed industries.'
  }
}

export const QA_SKIP_CERTS_LABEL = QA_SKIP_CERTS.map(
  cert => SUPPLIER_CERTIFICATE_LABELS[cert]
).join(', ')

export const SUPPLIER_CONTACT_ROLE_LABELS: Record<CompanyContactRole, string> =
  {
    ceo: 'CEO',
    customer_service: 'Customer Service',
    engineering: 'Engineering',
    operations: 'Operations',
    president: 'President',
    quality_assurance: 'Quality Assurance',
    sales: 'Sales',
    supply_chain: 'Supply Chain'
  }

/** order roles render in the dropdown. */
export const SUPPLIER_CONTACT_ROLES: ReadonlyArray<CompanyContactRole> = [
  'president',
  'ceo',
  'engineering',
  'sales',
  'quality_assurance',
  'customer_service',
  'operations',
  'supply_chain'
]

export const SUPPLIER_REVENUE_RANGE_LABELS: Record<
  SupplierRevenueRange,
  string
> = {
  lt_1m: 'Less than $1M',
  '1m_4_99m': '$1M - $4.99M',
  '5m_9_99m': '$5M - $9.99M',
  '10m_24_99m': '$10M - $24.99M',
  '25m_49_99m': '$25M - $49.99M',
  '50m_99_99m': '$50M - $99.99M',
  gte_100m: '$100M or more'
}

export const SUPPLIER_REVENUE_RANGES: ReadonlyArray<SupplierRevenueRange> = [
  'lt_1m',
  '1m_4_99m',
  '5m_9_99m',
  '10m_24_99m',
  '25m_49_99m',
  '50m_99_99m',
  'gte_100m'
]

export const SUPPLIER_BUILDING_SIZE_UNIT_LABELS: Record<
  SupplierBuildingSizeUnit,
  string
> = {
  sq_ft: 'sq ft',
  sq_m: 'sq m'
}

export const SUPPLIER_MEASUREMENT_EQUIPMENT_OPTIONS: Record<
  SupplierMeasurementEquipment,
  LabeledOption
> = {
  cmm: {
    label: 'CMM',
    description: 'Coordinate measuring machine'
  },
  hardness_testing: {
    label: 'Hardness testing',
    description: 'Rockwell, Brinell, Vickers hardness testing'
  },
  micro_hardness_testing: {
    label: 'Micro hardness testing',
    description: 'Micro-Vickers and Knoop hardness measurement'
  },
  microscope: {
    label: 'Microscope',
    description: 'Optical or electron microscopy'
  },
  spectrograph: {
    label: 'Spectrograph',
    description: 'Material composition analysis'
  },
  salt_spray_tester: {
    label: 'Salt spray tester',
    description: 'Corrosion resistance testing'
  },
  magnetic_particle: {
    label: 'Magnetic particle',
    description: 'Surface and near-surface flaw detection'
  },
  surface_finish: {
    label: 'Surface finish',
    description: 'Surface roughness profilometry'
  },
  penetrant: {
    label: 'Penetrant',
    description: 'Dye penetrant inspection'
  },
  optical_comparator: {
    label: 'Optical comparator',
    description: 'Profile and contour measurement'
  },
  chemical: {
    label: 'Chemical',
    description: 'Chemical composition analysis'
  },
  x_ray: {
    label: 'X-ray',
    description: 'Radiographic inspection'
  },
  calipers_micrometers: {
    label: 'Calipers and micrometers',
    description: 'Manual dimensional measurement'
  }
}

export const SUPPLIER_MEASUREMENT_EQUIPMENT_KINDS: ReadonlyArray<SupplierMeasurementEquipment> =
  [
    'cmm',
    'hardness_testing',
    'micro_hardness_testing',
    'microscope',
    'spectrograph',
    'salt_spray_tester',
    'magnetic_particle',
    'surface_finish',
    'penetrant',
    'optical_comparator',
    'chemical',
    'x_ray',
    'calipers_micrometers'
  ]
